import { useContext, useEffect } from 'react'
import Head from 'next/head'
import Script from 'next/script'
import Link from 'next/link'
import Image from 'next/image'
import { s3Static } from '../../utils/constants'
import { BlogHeaderLinks } from '../../components/blogs/BlogHeaderLinks'
import { contentDate } from '../../utils/article-date-time'
import { BlogsList } from '../../components/blogs/BlogList'
import { blogsMaster } from '../../utils/sg-blogs-data'
import { getWpNewPostsByCategory, getWpPostsByCategory } from '../../lib/service'
import { getWpHealthFitness, getWpNewHealthFitness, setWpHealthFitness, setWpNewHealthFitness } from '../../service/wp-blogs-store'

// When this page's content last actually changed. Bump it by hand when you
// edit the copy - not for layout/UX changes. Feeds dateModified and
// article:modified_time (see reviewedIso in utils/article-date-time).
const CONTENT_REVIEWED = '2026-09-22'

export async function getStaticProps() {

    const { modifiedDate, month, year } = contentDate(CONTENT_REVIEWED);
    const fitnessBlogs = blogsMaster['health-fitness'].blogArray;
    const wpPosts = getWpHealthFitness().length > 0 ?  getWpHealthFitness() : await getWpPostsByCategory(6, "health-fitness");
    setWpHealthFitness(wpPosts)
    const wpNewPosts = getWpNewHealthFitness().length > 0 ?  getWpNewHealthFitness() : await getWpNewPostsByCategory(6, "health-fitness");
    setWpNewHealthFitness(wpNewPosts)

  return {
      props: {
        modifiedDate,
        month,
        year,
        fitnessBlogs,
        wpPosts,
        wpNewPosts
      },
      // Next.js will attempt to re-generate the page:
      // - When a request comes in
      // - At most once every 10 seconds
  }
}

export default function Home(props) {
    const modifiedDate = props.modifiedDate
    const month = props.month
    const year = props.year
    const fitnessBlogs = props.fitnessBlogs
    const wpPosts = props.wpPosts
    const wpNewPosts = props.wpNewPosts

  return (
    <>
      <Head>
        <title>Low Intensity Interval Training (LIIT) vs HIIT</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1, minimum-scale=1.0, maximum-scale=1.0, user-scalable=no"/>
        <meta name="description" content="Should you be doing Low Intensity Interval Training instead of HIIT? Find out more about the effectiveness of LIIT."/>
        <meta name="keywords" content="House Rental, Insurance Plan, Personal Trainer, Maid Service, Handyman Service, Aircon Service, Savings Plan, Food Catering, Interior Design, Wedding Planner, Event Planner"/>
        <meta name="author" content="StringsSG"/>
        <meta name="google-site-verification" content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:url"   content="https://www.stringssg.com/blogs/low-intensity-interval-trainer-liit" />
        <meta property="og:type"   content="article" />
        <meta property="og:title"    content={`Low Intensity Interval Training (LIIT) vs HIIT`}/>
        <meta property="og:description" content="Should you be doing Low Intensity Interval Training instead of HIIT? Find out more about the effectiveness of LIIT." />
        <meta property="article:publisher" content="https://www.facebook.com/StringsSG/" />
        <meta property="article:published_time" content="2025-01-08T07:30:46+00:00" />
        <meta property="article:modified_time" content="2025-01-04T07:30:46+00:00" />
        <meta property="og:image"  content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/blog/health+and+fitness/girl+squat.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/blog/health+and+fitness/girl+squat.jpg" />
        <meta name="twitter:site" content="@stringssg"/> 
        <meta name="twitter:url" content="https://www.stringssg.com/blogs/low-intensity-interval-trainer-liit"/>
        <meta name="twitter:title" content={`Low Intensity Interval Training (LIIT) vs HIIT`}/> 
        <meta name="twitter:description" content="Should you be doing Low Intensity Interval Training instead of HIIT? Find out more about the effectiveness of LIIT."/>
        <link rel="icon" href="/favicon.ico" />
        <link rel="canonical" href="https://www.stringssg.com/blogs/low-intensity-interval-trainer-liit" />
        <script type="application/ld+json"
          dangerouslySetInnerHTML={{ __html:
            `{"@context":"https://schema.org","@graph":[{"@context":"https://schema.org/","@type":"BlogPosting","mainEntityOfPage":{"@type":"WebPage","@id":"https://www.stringssg.com/blogs/low-intensity-interval-trainer-liit"},"headline":"Low Intensity Interval Training (LIIT) vs HIIT","description":"Should you be doing Low Intensity Interval Training instead of HIIT? Find out more about the effectiveness of LIIT.","image":["https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/blog/health+and+fitness/girl+squat.jpg"],"author":{"@type":"Organization","@id":"https://www.stringssg.com/#organization","name":"StringsSG","url":"https://www.stringssg.com/"},"publisher":{"@type":"Organization","@id":"https://www.stringssg.com/#organization","name":"StringsSG","url":"https://www.stringssg.com/","logo":{"@type":"ImageObject","url":"https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/logo.png"}},"datePublished":"2025-01-08T07:30:46+00:00","dateModified":"2022-09-04T07:30:46+00:00"},{"@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"name":"Blogs","item":"https://www.stringssg.com/blogs"},{"@type":"ListItem","position":1,"name":"LIIT"}]}]}`
          }}
        />
      </Head>
      <div className='max-w-5xl mx-auto py-5 px-2'>
            <div className='space-y-2'>
                <h1 className='text-lg text-gray-500 font-semibold'>Low Intensity Training vs High Intensity Training</h1>
                <div className='text-gray-400 text-xs'>
                    <time dateTime="2022-09-08 00:00">September 8th 2022</time>
                </div>
            </div>
            <div className='py-2 text-gray-500 leading-8 space-y-2'>
                <div className='py-2'>
                    <p className=''>
                    We all are aware of the Rabbit vs Turtle race. This is a learning lesson in our day to day practical life. When it comes to exercise or fitness training, we might start off with full stamina and confidence but get tired or injured very soon and end up taking long breaks from our workout routine to recover. What happens in the process is we fall prey to bad internal damages. Today, it is much wiser to follow the ‘Turtle way’ and go slow but steady in order to win the race of a fit life.
                    </p>
                </div>
                <div className='relative h-96 max-w-xl'>
                    <Image className='rounded-lg'
                          src={s3Static + "blog/health+and+fitness/girl+squat.jpg"}
                          fill
                          style={{objectFit: 'cover'}}
                          placeholder='blur'
                          blurDataURL={s3Static + "placeholder.png"}
                          alt='girl doing squats'
                    />          
                </div>
                <div className='py-2 space-y-2'>
                    <h2 className='font-bold uppercase'>
                        LIIT or HIIT
                    </h2>
                    <p>
                        There has been a significant shift from HIIT to LIIT off late realizing the benefits of the later. HIITis definitely effective, but it comes along with the slogan-“No pain no gain”. For many of us specially, people with some prior injuries or elderly, HIIT thus become a big challenge. Studies show today, many people prefer to choose LIIT over HIIT.
                    </p>
                </div>
                <div className='py-2 space-y-2'>
                    <h2 className='font-bold uppercase'>
                        LIIT – THE BENEFITS
                    </h2>
                    <p>
                        There has been a significant shift from HIIT to LIIT off late realizing the benefits of the later. HIITis definitely effective, but it comes along with the slogan-“No pain no gain”. For many of us specially, people with some prior injuries or elderly, HIIT thus become a big challenge. Studies show today, many people prefer to choose LIIT over HIIT.
                    </p>
                    <ul className='list-decimal space-y-2 p-6'>
                        <li>
                        It is the best form of workout for beginners or people recovering from any injury.
                        </li>
                        <li>
                        These workouts are programmed with low intensity thus are easier on our joints.
                        </li>
                        <li>
                        It can be easily opted by elderly people who cannot go for high intensity workouts.
                        </li>
                        <li>
                        Since it leaves us with almost no injuries, we can be more consistent with our workout routine.
                        </li>
                        <li>
                        LIIT helps to build up stamina before you opt for higher intensity exercises.
                        </li>
                        <li>
                        It is very helpful for people having issues like anaemia, thyroid etc.
                        </li>
                        <li>
                        It has a steady positive result and thus brings us close to that fit self we dream of.
                        </li>
                    </ul>
                </div>
                <div className='py-2 space-y-2'>
                    <p>
                    However, every body type is different from one another and so is our demands and capacity. Whether you choose HIIT or LIIT the main idea is to work towards leading a healthy and fit life. So don’t let your today ruin your tomorrow. Get up, find yourself a fitness coach or a  workout partner or a fitness trainer and achieve an overall health and fitness all year round!
                    </p>
                </div>
                <div className='py-2 text-center'>
                    <Link href="/sg/personal-trainer" className='px-3 py-3 border border-amber-600 text-orange-600 rounded-lg'>
                            Hire a personal trainer in Singapore
                    </Link>
                </div>
            </div>
            <h2 className='text-center text-xl uppercase pt-10'>You may also like</h2>
            <BlogsList blogs={fitnessBlogs} wpPosts={wpPosts} wpNewPosts={wpNewPosts}/>
        </div>
        <div className='fixed bottom-0 w-full text-center text-lg'>
            <Link href="/sg/personal-trainer" prefetch={false} className='text-white py-3 bg-linear-to-tr from-amber-600 to-amber-400 block font-bold w-full'>
                    Hire a personal trainer in Singapore
            </Link>
        </div>
        <BlogHeaderLinks/>
        <Script 
          id="google-tagmanager-new"
          src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
          strategy="lazyOnload"
          onLoad={() => {              
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
          
            gtag('config', 'G-QD1HGGHDYJ');
          }} 
        />
        <Script id="facebook-tracking" strategy="lazyOnload">
          {`!function(f,b,e,v,n,t,s)
					  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
					  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
					  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
					  n.queue=[];t=b.createElement(e);t.async=!0;
					  t.src=v;s=b.getElementsByTagName(e)[0];
					  s.parentNode.insertBefore(t,s)}(window, document,'script',
					  'https://connect.facebook.net/en_US/fbevents.js');
					  fbq('init', '128634041116395');
					  fbq('track', 'PageView');`}
        </Script>
    </>
  )
}
